import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  Select,
  InputAdornment,
  IconButton,
  FormHelperText,
  Stack,
  LinearProgress,
  CircularProgress
} from '@mui/material';
import { useFormik } from 'formik';
import { hotelSchema } from 'schema';
import { postApi } from 'views/services/api';
import { toast } from 'react-toastify';
import { getTimezones } from 'utils/timeZone';
import { getCountries, getCountryDialCode, buildInternationalPhone } from 'utils/countries';
import PropTypes from 'prop-types';

// icons
import ClearIcon from '@mui/icons-material/Clear';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const AddHotel = (props) => {
  const { open, handleClose } = props;
  const [showPassword, setShowPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const initialValues = {
    email: '',
    password: '',
    name: '',
    managerName: '',
    contact: '',
    managerPhone: '',
    address: '',
    country: '',
    role: 'HotelAdmin',
    currency: 'USD',
    timezone: 'America/Caracas'
  };

  const handleAddHotel = async (values, resetForm) => {
    setIsSubmitting(true);
    try {
      const response = await postApi('api/hotel/register', values);
      if (response.status === 200) {
        toast.success('¡Hotel registrado exitosamente!');
        resetForm();
        setTimeout(() => {
          handleClose();
        }, 500);
      } else {
        toast.error(response.response?.data?.message || 'Error al registrar el hotel');
      }
    } catch (err) {
      console.log(err);
      toast.error('Ocurrió un error inesperado al intentar registrar el hotel');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: hotelSchema,
    onSubmit: async (values, { resetForm }) => {
      const payload = {
        ...values,
        email: values.email.toLowerCase(),
        // Formato internacional para lead + Sego IA (WhatsApp).
        contact: buildInternationalPhone(values.country, values.contact),
        managerPhone: buildInternationalPhone(values.country, values.managerPhone)
      };
      handleAddHotel(payload, resetForm);
    }
  });

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: { borderRadius: '12px', overflow: 'hidden' }
      }}
    >
      <DialogTitle sx={{ 
        m: 0, 
        p: 2, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
        color: 'white'
      }}>
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 600 }}>
          Registrar Nuevo Hotel
        </Typography>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'white' }}>
          <ClearIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 4 }}>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={3}>
            {/* Email */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.email && Boolean(formik.errors.email)}>
                <InputLabel>Correo Electrónico</InputLabel>
                <OutlinedInput
                  id="email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <PersonOutlineIcon color="action" />
                    </InputAdornment>
                  }
                  label="Correo Electrónico"
                  placeholder="ejemplo@hotel.com"
                />
                {formik.touched.email && formik.errors.email && (
                  <FormHelperText error>{formik.errors.email}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Password */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.password && Boolean(formik.errors.password)}>
                <InputLabel>Contraseña</InputLabel>
                <OutlinedInput
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <LockOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Contraseña"
                  placeholder="Mínimo 8 caracteres"
                />
                {formik.touched.password && formik.errors.password && (
                  <FormHelperText error>{formik.errors.password}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Name */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.name && Boolean(formik.errors.name)}>
                <InputLabel>Nombre del Hotel</InputLabel>
                <OutlinedInput
                  id="name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Nombre del Hotel"
                  placeholder="Nombre oficial del establecimiento"
                />
                {formik.touched.name && formik.errors.name && (
                  <FormHelperText error>{formik.errors.name}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Persona Encargada */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Persona Encargada</InputLabel>
                <OutlinedInput
                  id="managerName"
                  name="managerName"
                  value={formik.values.managerName}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <BadgeOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Persona Encargada"
                  placeholder="Responsable / contacto del hotel"
                />
              </FormControl>
            </Grid>

            {/* País — antes de los teléfonos para derivar el prefijo */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>País</InputLabel>
                <Select
                  id="country"
                  name="country"
                  native
                  value={formik.values.country}
                  onChange={formik.handleChange}
                  input={
                    <OutlinedInput
                      label="País"
                      startAdornment={
                        <InputAdornment position="start">
                          <PublicOutlinedIcon color="action" />
                        </InputAdornment>
                      }
                    />
                  }
                >
                  <option value="">Selecciona un país</option>
                  {getCountries().map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={formik.touched.contact && Boolean(formik.errors.contact)}>
                <InputLabel>Teléfono del Hotel</InputLabel>
                <OutlinedInput
                  id="contact"
                  name="contact"
                  type="text"
                  value={formik.values.contact}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <PhoneIphoneOutlinedIcon color="action" />
                      {getCountryDialCode(formik.values.country) && (
                        <Box component="span" sx={{ ml: 0.5, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {getCountryDialCode(formik.values.country)}
                        </Box>
                      )}
                    </InputAdornment>
                  }
                  label="Teléfono del Hotel"
                  placeholder={getCountryDialCode(formik.values.country) ? 'Número local' : 'Ej: +58 212...'}
                />
                {formik.touched.contact && formik.errors.contact && (
                  <FormHelperText error>{formik.errors.contact}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Teléfono del Encargado — prefijo derivado del país */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Teléfono del Encargado</InputLabel>
                <OutlinedInput
                  id="managerPhone"
                  name="managerPhone"
                  value={formik.values.managerPhone}
                  onChange={formik.handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <PhoneIphoneOutlinedIcon color="action" />
                      {getCountryDialCode(formik.values.country) && (
                        <Box component="span" sx={{ ml: 0.5, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                          {getCountryDialCode(formik.values.country)}
                        </Box>
                      )}
                    </InputAdornment>
                  }
                  label="Teléfono del Encargado"
                  placeholder={getCountryDialCode(formik.values.country) ? 'Número local (también autoriza Sego IA)' : 'Ej: +58 414...'}
                />
              </FormControl>
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <FormControl fullWidth error={formik.touched.address && Boolean(formik.errors.address)}>
                <InputLabel>Dirección Física</InputLabel>
                <OutlinedInput
                  id="address"
                  name="address"
                  value={formik.values.address}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  startAdornment={
                    <InputAdornment position="start">
                      <LocationOnOutlinedIcon color="action" />
                    </InputAdornment>
                  }
                  label="Dirección Física"
                  placeholder="Calle, Ciudad, Estado/País"
                  multiline
                  rows={2}
                />
                {formik.touched.address && formik.errors.address && (
                  <FormHelperText error>{formik.errors.address}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Currency */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Moneda Principal</InputLabel>
                <Select
                  id="currency"
                  name="currency"
                  native
                  value={formik.values.currency}
                  onChange={formik.handleChange}
                  input={
                    <OutlinedInput 
                      label="Moneda Principal" 
                      startAdornment={
                        <InputAdornment position="start">
                          <MonetizationOnOutlinedIcon color="action" />
                        </InputAdornment>
                      }
                    />
                  }
                >
                  <option value="USD">USD - Dólar estadounidense</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="MXN">MXN - Peso mexicano</option>
                  <option value="ARS">ARS - Peso argentino</option>
                  <option value="BOB">BOB - Boliviano</option>
                  <option value="BRL">BRL - Real brasileño</option>
                  <option value="CLP">CLP - Peso chileno</option>
                  <option value="COP">COP - Peso colombiano</option>
                  <option value="CRC">CRC - Colón costarricense</option>
                  <option value="CUP">CUP - Peso cubano</option>
                  <option value="DOP">DOP - Peso dominicano</option>
                  <option value="GTQ">GTQ - Quetzal guatemalteco</option>
                  <option value="HNL">HNL - Lempira hondureño</option>
                  <option value="NIO">NIO - Córdoba nicaragüense</option>
                  <option value="PAB">PAB - Balboa panameño</option>
                  <option value="PEN">PEN - Sol peruano</option>
                  <option value="PYG">PYG - Guaraní paraguayo</option>
                  <option value="UYU">UYU - Peso uruguayo</option>
                  <option value="VES">VES - Bolívar venezolano</option>
                </Select>
              </FormControl>
            </Grid>

            {/* Timezone */}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Zona Horaria</InputLabel>
                <Select
                  id="timezone"
                  name="timezone"
                  native
                  value={formik.values.timezone}
                  onChange={formik.handleChange}
                  input={
                    <OutlinedInput 
                      label="Zona Horaria" 
                      startAdornment={
                        <InputAdornment position="start">
                          <LanguageOutlinedIcon color="action" />
                        </InputAdornment>
                      }
                    />
                  }
                >
                  {getTimezones().map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {isSubmitting && (
            <Box sx={{ width: '100%', mt: 3 }}>
              <LinearProgress />
            </Box>
          )}
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 3, backgroundColor: '#f5f5f5' }}>
        <Button onClick={handleClose} variant="text" color="inherit">
          Cancelar
        </Button>
        <Button 
          onClick={formik.handleSubmit} 
          variant="contained" 
          disabled={isSubmitting}
          sx={{ 
            minWidth: 150,
            background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
            boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #1976D2 30%, #2196F3 90%)',
            }
          }}
        >
          {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Registrar Hotel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AddHotel.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired
};

export default AddHotel;
